import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Skeleton } from '@mui/material';
import Dayjs from 'dayjs';
import PageContainer from '../../components/PageContainer';
import { EmptyState, PageHeader, Panel } from '../../components/ui';
import { Orders } from '../../store/slices';
import { IStore } from '../../store/types';
import OrdersCountTable from '../../components/OrdersCountTable';
import OrdersCountFilters from '../../components/OrdersCountFilters';

const todayRange = () => ({
    maxDate: Dayjs(new Date()).format('YYYY-MM-DD'),
    minDate: Dayjs(new Date()).format('YYYY-MM-DD'),
});

/** Units per client per day from order-api (`/analytics/units/by-client/daily`). URL-only page. */
const OrdersAnalyticsPage = () => {
    const { orders, status, queryParams } = useSelector<IStore, { orders: any[]; status: string; queryParams: any }>((state) => ({
        orders: state.orders.ordersAnalytics,
        status: state.orders.status,
        queryParams: state.orders.ordersAnalyticsParams,
    }));
    const dispatch = useDispatch();
    const [filtersKey, setFiltersKey] = useState(0);

    const loadOrders = (params: any) => {
        dispatch<any>(Orders.getOrdersAnalytics(params));
    };

    const clearFilters = () => {
        dispatch(Orders.clearFilters());
        setFiltersKey((key) => key + 1);
        loadOrders(todayRange());
    };

    useEffect(() => {
        const defaults = todayRange();
        loadOrders({
            ...queryParams,
            minDate: queryParams?.minDate || defaults.minDate,
            maxDate: queryParams?.maxDate || defaults.maxDate,
        });
        // Mount-only load, as before; the filters reload on their own.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const params = { ...todayRange(), ...queryParams };
    const loading = status === 'loading';
    const list = Array.isArray(orders) ? orders : [];

    return (
        <PageContainer constrained>
            <PageHeader eyebrow="API Integration" title="Units by client" subtitle="Ordered units per client for each day in the range." />
            <Panel padding="none">
                <OrdersCountFilters key={filtersKey} queryParams={params} loadOrders={loadOrders} loading={loading} onClear={clearFilters} />
                {loading && (
                    <div style={{ padding: 16, display: 'grid', gap: 10 }} role="status" aria-label="Loading report">
                        {[0, 1, 2, 3].map((row) => <Skeleton key={row} animation="wave" height={44} />)}
                    </div>
                )}
                {!loading && list.length > 0 && <OrdersCountTable orders={list} />}
                {!loading && list.length === 0 && (
                    <EmptyState
                        title={status === 'rejected' ? 'The report could not be loaded' : 'No units in this range'}
                        hint={status === 'rejected' ? 'The order service did not answer. Try again in a moment.' : 'Widen the date range to see more days.'}
                        action={status === 'rejected' ? <Button variant="outlined" size="small" onClick={() => loadOrders(params)}>Retry</Button> : undefined}
                    />
                )}
            </Panel>
        </PageContainer>
    );
};

export default OrdersAnalyticsPage;
